import { instance } from "api/apiBase";
import { PropertyKind } from "shared/types";
import { Address, ApartmentsComVariant } from "types/landlord/address";

export type SyndicationFeed =
  | "zillow"
  | "zumper"
  | "apartments_com"
  | "dwellsy"
  | "affordable_housing_com"
  | "rental_beast"
  | "uloop"
  | "rental_source";

/**
 * A connect request names either the listings to include or the ones to hold back,
 * whichever is shorter — see `listingSelection.ts`.
 */
export type FeedConnectPayload =
  | { unit_ids: number[] }
  | { excluded_unit_ids: number[] };

export interface FeedConnectionStatus {
  connected: boolean;
  auto_add_listings: boolean;
}

interface FeedMoney {
  cents: number;
  currency: string;
}

export interface FeedListing {
  id: number;
  title: string | null;
  active: boolean;
  monthly_rent_amount: FeedMoney | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  feed_zillow_enabled: boolean;
  feed_zillow_spotlight_enabled: boolean;
  feed_zumper_enabled: boolean;
  feed_apartments_com_enabled: boolean;
  feed_apartments_com_variant: ApartmentsComVariant | null;
  feed_dwellsy_enabled: boolean;
  feed_affordable_housing_com_enabled: boolean;
  feed_rental_beast_enabled: boolean;
  feed_uloop_enabled: boolean;
  feed_uloop_featured_enabled: boolean;
  feed_rental_source_enabled: boolean;
  feed_zillow_enabled_at: string | null;
  feed_zumper_enabled_at: string | null;
  feed_apartments_com_enabled_at: string | null;
  feed_dwellsy_enabled_at: string | null;
  feed_affordable_housing_com_enabled_at: string | null;
  feed_rental_beast_enabled_at: string | null;
  feed_uloop_enabled_at: string | null;
  feed_rental_source_enabled_at: string | null;
  unit: { id: number; title: string | null; listed: boolean } | null;
  property: {
    id: number;
    name: string;
    property_group: { id: number; name: string; index: number } | null;
    address: Address | null;
    owner: string | null;
    kind: PropertyKind | null;
  };
}

interface FeedListingsPage {
  items: FeedListing[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
}

interface ToggleFeedListingPayload {
  enabled?: boolean;
  variant?: ApartmentsComVariant;
  spotlight_enabled?: boolean;
  featured_enabled?: boolean;
}

// The timestamp column name differs per feed; map it once so callers can read
// the right "listed on" field without string-templating into the type.
export const FEED_ENABLED_AT_FIELD: Record<
  SyndicationFeed,
  | "feed_zillow_enabled_at"
  | "feed_zumper_enabled_at"
  | "feed_apartments_com_enabled_at"
  | "feed_dwellsy_enabled_at"
  | "feed_affordable_housing_com_enabled_at"
  | "feed_rental_beast_enabled_at"
  | "feed_uloop_enabled_at"
  | "feed_rental_source_enabled_at"
> = {
  zillow: "feed_zillow_enabled_at",
  zumper: "feed_zumper_enabled_at",
  apartments_com: "feed_apartments_com_enabled_at",
  dwellsy: "feed_dwellsy_enabled_at",
  affordable_housing_com: "feed_affordable_housing_com_enabled_at",
  rental_beast: "feed_rental_beast_enabled_at",
  uloop: "feed_uloop_enabled_at",
  rental_source: "feed_rental_source_enabled_at"
};

const feedBaseUrl = (feed: SyndicationFeed) =>
  `/portal/v1/external_integrations/${feed}`;

const feedListingsUrl = (feed: SyndicationFeed) =>
  `${feedBaseUrl(feed)}/listings`;

export const syndicationFeedApi = {
  getFeedStatus: async (feed: SyndicationFeed): Promise<FeedConnectionStatus> =>
    (await instance.get<FeedConnectionStatus>(feedBaseUrl(feed))).data,

  updateFeedSettings: async (
    feed: SyndicationFeed,
    payload: { auto_add_listings: boolean }
  ): Promise<FeedConnectionStatus> =>
    (
      await instance.patch<FeedConnectionStatus>(
        `${feedBaseUrl(feed)}/settings`,
        payload
      )
    ).data,

  getFeedListings: async (
    feed: SyndicationFeed,
    { page = 1, per_page = 50 }: { page?: number; per_page?: number } = {}
  ): Promise<FeedListingsPage> =>
    (
      await instance.get<FeedListingsPage>(feedListingsUrl(feed), {
        params: { page, per_page }
      })
    ).data,

  toggleFeedListing: async (
    feed: SyndicationFeed,
    unitId: number,
    payload: ToggleFeedListingPayload
  ): Promise<FeedListing> =>
    (
      await instance.patch<FeedListing>(
        `${feedListingsUrl(feed)}/${unitId}`,
        payload
      )
    ).data
};
